import { T, formatCents, formatCount, formatDateTime, formatDay, formatPercent } from '@agos/shared'

import {
  cappedNote,
  openOrdersNote,
  owedRows,
  sheetDisagrees,
  splitSentence,
  type CashbackReport,
} from './organization-cashback'

/**
 * The cashback report as a handover sheet - `FRONTEND.md` §9.783, owner O138g.
 *
 * Drawn only on paper: the print stylesheet hides everything else on the page and this element is hidden on screen,
 * the trip log's pattern. It prints what the organization is owed and why, and leaves the amount handed over and the
 * signatures as lines to write on - the payout is recorded in *Zaznamenať výplatu*, and a sheet printing an amount
 * nobody has paid yet would be a receipt for money that has not moved.
 */

export const SHEET_ID = 'cashback-sheet-print'

export const SHEET_STYLES = `
#${SHEET_ID} { display: none; }
@media print {
  @page { size: A4 portrait; margin: 14mm; }
  body * { visibility: hidden; }
  #${SHEET_ID}, #${SHEET_ID} * { visibility: visible; }
  #${SHEET_ID} { display: block; position: absolute; top: 0; left: 0; width: 100%; color: #000; background: #fff; }
}
`

export function CashbackSheet({
  report,
  restaurantName,
  printedOn,
}: {
  report: CashbackReport
  restaurantName: string
  printedOn: string
}) {
  const organization = report.organization
  const rows = owedRows(report)
  const notes = [openOrdersNote(report), cappedNote(report)].filter((note): note is string => note !== null)
  const cell = 'py-1 pr-2 border-b border-black/20'

  return (
    <div id={SHEET_ID} className="text-[11px] leading-snug space-y-4" data-cashback-sheet={organization.couponCode}>
      <div className="flex items-start justify-between gap-4">
        <p className="font-bold text-sm" data-sheet-restaurant>
          {restaurantName}
        </p>
        <p data-sheet-printed>
          <T>Vytlačené</T> {printedOn}
        </p>
      </div>

      <div className="space-y-0.5">
        <h1 className="text-lg font-bold">
          <T>Výkaz podielu pre organizáciu</T>
        </h1>
        <p className="text-base font-bold" data-sheet-organization>
          {organization.name}
        </p>
        <p>
          <T>Kód kupónu</T>: <span className="font-mono font-bold">{organization.couponCode}</span> ·{' '}
          {splitSentence(organization)}
        </p>
        <p data-sheet-period>
          <T>Obdobie</T>: {report.from === '' ? '-' : formatDay(report.from)} -{' '}
          {report.to === '' ? '-' : formatDay(report.to)}
        </p>
      </div>

      {rows.length === 0 ? (
        <p data-sheet-empty>
          <T>V tomto období organizácii nepatrí podiel zo žiadnej objednávky.</T>
        </p>
      ) : (
        <table className="w-full border-collapse" data-sheet-rows>
          <thead>
            <tr className="text-left">
              <th className={cell}>
                <T>Dátum</T>
              </th>
              <th className={cell}>
                <T>Objednávka</T>
              </th>
              <th className={`${cell} text-right`}>
                <T>Jedlo</T>
              </th>
              <th className={`${cell} text-right`}>%</th>
              <th className={`${cell} text-right`}>
                <T>Podiel</T>
              </th>
            </tr>
          </thead>
          <tbody>
            {rows.map((order) => (
              <tr key={order.orderId} data-sheet-row={order.orderNumber}>
                <td className={cell}>{order.createdAt === '' ? '-' : formatDateTime(order.createdAt)}</td>
                <td className={cell}>#{order.orderNumber}</td>
                <td className={`${cell} text-right tabular-nums`}>{formatCents(order.foodCents)}</td>
                <td className={`${cell} text-right tabular-nums`}>{formatPercent(order.organizationPercent)}</td>
                <td className={`${cell} text-right tabular-nums`}>{formatCents(order.shareCents)}</td>
              </tr>
            ))}
          </tbody>
          <tfoot>
            {/* The server's own sum for the period, not a sum of the printed rows: with the list capped the two
                differ, and the cap is said below. */}
            <tr className="font-bold" data-sheet-total>
              <td className="py-1 pr-2" colSpan={2}>
                <T>Spolu za obdobie</T> ({formatCount(report.owed.orders)})
              </td>
              <td className="py-1 pr-2 text-right tabular-nums">{formatCents(report.owed.foodCents)}</td>
              <td className="py-1 pr-2" />
              <td className="py-1 pr-2 text-right tabular-nums" data-sheet-total-share>
                {formatCents(report.owed.shareCents)}
              </td>
            </tr>
          </tfoot>
        </table>
      )}

      {notes.map((note) => (
        <p key={note} data-sheet-note>
          {note}
        </p>
      ))}
      {sheetDisagrees(report) && (
        <p className="font-bold" data-sheet-disagrees>
          <T>Riadky sa nezhodujú so súčtom za obdobie - pred odovzdaním obnovte report a vytlačte ho znova.</T>
        </p>
      )}

      <div className="space-y-1" data-sheet-account>
        <p className="font-bold">
          <T>Stav účtu organizácie ku dňu tlače</T>
        </p>
        <div className="grid grid-cols-3 gap-2">
          <p>
            <T>Patrí jej spolu</T>: <strong className="tabular-nums">{formatCents(organization.owedCents)}</strong>
          </p>
          <p>
            <T>Vyplatené</T>: <strong className="tabular-nums">{formatCents(organization.paidCents)}</strong>
          </p>
          <p data-sheet-balance>
            <T>Zostáva vyplatiť</T>:{' '}
            <strong className="tabular-nums">{formatCents(organization.balanceCents)}</strong>
          </p>
        </div>
        {report.payouts.length === 0 ? (
          <p>
            <T>Zatiaľ nič nevyplatené.</T>
          </p>
        ) : (
          <ul data-sheet-payouts>
            {report.payouts.map((payout) => (
              <li key={payout.id}>
                {formatDay(payout.paidOn)} · {formatCents(payout.amountCents)}
                {payout.note !== '' && ` · ${payout.note}`}
              </li>
            ))}
          </ul>
        )}
      </div>

      <p data-sheet-basis>
        <T>
          Podiel sa počíta z ceny jedla objednávok s týmto kódom, ktoré sa už nedajú zrušiť (doručené alebo
          vyzdvihnuté), percentom platným v čase objednávky.
        </T>
      </p>

      <div className="space-y-6 pt-2" data-sheet-handover>
        <div className="grid grid-cols-2 gap-6">
          <p>
            <T>Odovzdaná suma</T>: ______________________ €
          </p>
          <p>
            <T>Dňa</T>: ______________________
          </p>
        </div>
        <div className="grid grid-cols-2 gap-6 pt-6">
          <div>
            <p className="border-t border-black pt-1">
              <T>Za reštauráciu (odovzdal)</T>
            </p>
          </div>
          <div>
            <p className="border-t border-black pt-1">
              <T>Za organizáciu (prevzal)</T>
            </p>
          </div>
        </div>
      </div>
    </div>
  )
}
